import React, { useState } from "react";
import "./CharterPartyAgreementModal.css";

const CharterPartyAgreementModal = ({ 
  isOpen, 
  onClose, 
  onSign, 
  isLoading,
  charterRequest,
  profileData 
}) => {
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signatureData, setSignatureData] = useState({
    representativeName: "",
    signatureDate: new Date().toISOString().split('T')[0]
  });

  if (!isOpen) return null;

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const handleReviewAndSign = () => {
    setShowSigningModal(true);
  };

  const handleAgreeAndSign = () => {
    if (!signatureData.representativeName.trim()) {
      return;
    }
    onSign();
  };

  return (
    <div className="charter-party-modal-overlay" onClick={onClose}>
      <div className="charter-party-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="charter-party-modal-header">
          <h2>CHARTER PARTY AGREEMENT</h2>
        </div>
        
        <div className="charter-party-modal-body">
          <div className="agreement-intro">
            <p>
              THIS CHARTER PARTY AGREEMENT is made this {currentDate} between
            </p>
          </div>

          <div className="parties-section">
            <div className="party-column">
              <p>
                <strong>VATUG</strong> of Nigeria (hereinafter described as "VATUG" which expression shall, where the context so admits, include its successors-in-title and assigns), AND
              </p>
            </div>
            <div className="party-column">
              <p>
                <strong>{profileData?.companyName || "Charterer"}</strong>, of {profileData?.address || "Address"} {profileData?.city || ""} {profileData?.state || ""} {profileData?.country || ""}. (hereinafter described as "the Charterer" which expression shall, where the context so admits, include its successors-in-title and assignments).
              </p>
            </div>
          </div>

          <div className="whereas-section">
            <h3>WHEREAS</h3>
            <ol>
              <li>VATUG operates a vessel charter platform designed to facilitate vessel hire agreements between vessel owners and charterers.</li>
              <li>The Charterer has requested to hire a vessel for commercial purposes.</li>
              <li>The parties have agreed to enter into this Charter Party Agreement which shall govern the terms and conditions of the vessel hire.</li>
              <li>The Charter Details in Part A constitute part of this Agreement, and shall be read in conjunction with the terms in Part B.</li>
            </ol>
          </div>

          <div className="agreement-intro">
            <p>The Parties hereby agree as follows:</p>
          </div>

          {/* Part A: Charter Details */}
          <div className="part-a-section">
            <h3>PART A: CHARTER DETAILS</h3>
            <div className="charter-details-table">
              <div className="charter-row">
                <div className="charter-label">Name of Vessel</div>
                <div className="charter-value">{charterRequest?.vessel || "N/A"}</div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Charterer</div>
                <div className="charter-value">{profileData?.companyName || "N/A"}</div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Scope of Work</div>
                <div className="charter-value">{charterRequest?.scopeOfWork || "N/A"}</div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Charter Duration</div>
                <div className="charter-value">{charterRequest?.duration || "N/A"} days</div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Charter Period</div>
                <div className="charter-value">
                  {charterRequest?.startDate ? new Date(charterRequest.startDate).toLocaleDateString() : "N/A"} to {" "}
                  {charterRequest?.endDate ? new Date(charterRequest.endDate).toLocaleDateString() : "N/A"}
                </div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Location of Operation</div>
                <div className="charter-value">
                  {charterRequest?.origin?.name || charterRequest?.locations?.[0]?.name || "N/A"}
                </div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Charter Fee</div>
                <div className="charter-value">
                  {charterRequest?.finalProposedPrice ? `$${charterRequest.finalProposedPrice.toLocaleString()}` : "N/A"}
                </div>
              </div>
              <div className="charter-row">
                <div className="charter-label">Payment Terms</div>
                <div className="charter-value">As per agreed schedule</div>
              </div>
            </div>
          </div>

          {/* Part B: Standard Terms and Conditions */}
          <div className="part-b-section">
            <h3>PART B: STANDARD TERMS AND CONDITIONS</h3>

            <div className="terms-section">
              <h4>Definitions</h4>
              <ol>
                <li>"Vessel" means the vessel described in Part A hereof.</li>
                <li>"Charterer" means the party hiring the Vessel as described in Part A.</li>
                <li>"Charter Fee" means the fee payable by the Charterer for the hire of the Vessel as specified in Part A.</li>
                <li>"Charter Period" means the period during which the Vessel is chartered as specified in Part A.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Hire of Vessel</h4>
              <ol>
                <li>The Owner agrees to let and the Charterer agrees to hire the Vessel for the Charter Period upon the terms and conditions herein contained.</li>
                <li>The Charterer agrees to use the Vessel solely for the purposes specified in Part A and in accordance with all applicable laws and regulations.</li>
                <li>The Charterer shall not sub-charter, assign or part with the possession of the Vessel without the prior written consent of the Owner.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Payment Terms</h4>
              <ol>
                <li>The Charterer shall pay the Charter Fee as specified in Part A in accordance with the agreed payment schedule.</li>
                <li>All payments shall be made in USD unless otherwise agreed in writing.</li>
                <li>In the event of late payment, the Charterer shall be liable to pay interest at the rate of 1.5% per month on the overdue amount.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Vessel Condition and Maintenance</h4>
              <ol>
                <li>The Owner warrants that the Vessel is in good condition and fit for the purposes for which it is being chartered.</li>
                <li>The Charterer shall return the Vessel in the same condition as received, fair wear and tear excepted.</li>
                <li>The Charterer shall be responsible for all damages caused to the Vessel during the Charter Period.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Indemnity and Insurance</h4>
              <ol>
                <li>The Charterer shall indemnify the Owner against all losses, damages, costs and expenses arising from the Charterer's use of the Vessel.</li>
                <li>The Charterer shall maintain adequate insurance coverage for the duration of the Charter Period.</li>
                <li>The Owner shall maintain all necessary vessel insurance and certifications.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Termination</h4>
              <ol>
                <li>Either party may terminate this Agreement in the event of a material breach by the other party that remains unremedied for 30 days.</li>
                <li>Upon termination, the Charterer shall return the Vessel to the Owner at the specified location.</li>
                <li>All outstanding payments shall become immediately due upon termination.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Governing Law</h4>
              <ol>
                <li>This Agreement shall be governed by, and construed in accordance with, the laws of the Federal Republic of Nigeria.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h4>Dispute Resolution</h4>
              <ol>
                <li>If any dispute arises out of or in connection with this Agreement, the Parties shall resolve the dispute amicably within 30 calendar days.</li>
                <li>If for any reason the Parties are unable to resolve the dispute, the Parties agree to enter into mediation in good faith to settle such a dispute at the Rivers State Multi-Door Courthouse.</li>
                <li>No Party may commence any court proceedings arising out of this Agreement until it has attempted to settle the dispute by mediation and either the mediation has terminated or the other Party has failed to participate in the mediation.</li>
              </ol>
            </div>

            <div className="witness-clause">
              <p>In witness of the above, the Parties execute this Agreement on the date first above written</p>
            </div>
          </div>
        </div>

        <div className="charter-party-modal-footer">
          <button 
            className="btn-back" 
            onClick={onClose}
            disabled={isLoading}
          >
            Back
          </button>
          <button 
            className="btn-sign" 
            onClick={handleReviewAndSign}
            disabled={isLoading}
          >
            Review and Sign
          </button>
        </div>
      </div>

      {/* Signing Modal */}
      {showSigningModal && (
        <div className="signing-modal-overlay" onClick={() => setShowSigningModal(false)}>
          <div className="signing-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="signing-modal-header">
              <h3>Signing Agreement</h3>
              <button onClick={() => setShowSigningModal(false)}>&times;</button>
            </div>
            
            <div className="signing-modal-body">
              <div className="signing-parties">
                {/* VATUG Signature Section */}
                <div className="signing-party">
                  <div className="party-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <p className="on-behalf-of">on behalf of</p>
                  <p className="company-name">VATUG</p>
                  <div className="signature-field">
                    <label>Representative Name</label>
                    <input 
                      type="text" 
                      placeholder="Enter name"
                      disabled
                      value="VATUG Representative"
                    />
                  </div>
                  <div className="signature-field">
                    <label>Date</label>
                    <input 
                      type="date" 
                      disabled
                      value={signatureData.signatureDate}
                    />
                  </div>
                </div>

                {/* Charterer/Vessel Owner Signature Section */}
                <div className="signing-party">
                  <div className="party-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <p className="on-behalf-of">on behalf of</p>
                  <p className="company-name">{profileData?.companyName || "Company Name"}</p>
                  <div className="signature-field">
                    <label>Representative Name</label>
                    <input 
                      type="text" 
                      placeholder="Enter name"
                      value={signatureData.representativeName}
                      onChange={(e) => setSignatureData(prev => ({ ...prev, representativeName: e.target.value }))}
                    />
                  </div>
                  <div className="signature-field">
                    <label>Date</label>
                    <input 
                      type="date" 
                      value={signatureData.signatureDate}
                      onChange={(e) => setSignatureData(prev => ({ ...prev, signatureDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="signing-modal-footer">
              <button 
                className="btn-agree-sign" 
                onClick={handleAgreeAndSign}
                disabled={isLoading || !signatureData.representativeName.trim()}
              >
                {isLoading ? "Signing..." : "Agree and Sign"}
              </button>
              <button 
                className="btn-cancel-sign" 
                onClick={() => setShowSigningModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharterPartyAgreementModal;

